import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { DndContext, useDraggable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { toast } from "sonner";
import { Plus, Trash2, Circle, Square, RectangleHorizontal, X, UserRound, RotateCw, Pencil, LayoutGrid, Printer, ChevronDown, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { tokenSize, seatLabelPositions } from "@/lib/tableGeometry";
import { TableauPrint, FloorPlanPrint } from "./PrintViews";

const SHAPES = { round: "Tondo", square: "Quadrato", rect: "Rettangolare" };

// How close (px) a dragged table's edge/center must get to another's before it
// snaps into alignment with it (Canva-style guide lines).
const SNAP_THRESHOLD = 10;

// Minimum gap (px) enforced only to stop tokens visually overlapping -- much
// smaller than typical template spacing so the auto-separation barely nudges tables.
const OVERLAP_PAD = 10;

const CANVAS_W = 1200;
const CANVAS_H = 900;

// Parametric layout templates: each generates N table specs (shape/x/y) inside the
// 1200x900 canvas, scaled to the requested table count instead of a fixed layout.
// Spacing constants leave generous room for guest-name seat labels around each table.
const TEMPLATES = [
  {
    id: "grid-round",
    name: "Tavoli rotondi a griglia",
    desc: "Una griglia regolare di tavoli rotondi, adatta alla maggior parte delle sale.",
    generate: (n, capacity) => {
      const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
      return Array.from({ length: n }, (_, i) => ({
        shape: "round",
        capacity,
        x: 60 + (i % cols) * 280,
        y: 60 + Math.floor(i / cols) * 280,
      }));
    },
  },
  {
    id: "banquet-rows",
    name: "File rettangolari (banchetto)",
    desc: "Tavoli rettangolari allineati in file parallele, stile banchetto classico.",
    generate: (n, capacity) => {
      const perRow = 4;
      return Array.from({ length: n }, (_, i) => ({
        shape: "rect",
        capacity,
        x: 60 + (i % perRow) * 260,
        y: 60 + Math.floor(i / perRow) * 230,
      }));
    },
  },
  {
    id: "horseshoe",
    name: "Ferro di cavallo",
    desc: "Tavoli disposti a U attorno a uno spazio centrale, ideale per un tavolo d'onore.",
    generate: (n, capacity) => {
      const bottomCount = Math.max(2, Math.ceil(n / 3));
      const sideCount = Math.ceil((n - bottomCount) / 2);
      const tables = [];
      let i = 0;
      for (let k = 0; k < bottomCount && i < n; k++, i++) tables.push({ shape: "rect", capacity, x: 100 + k * 250, y: 680 });
      for (let k = 0; k < sideCount && i < n; k++, i++) tables.push({ shape: "rect", capacity, x: 100, y: 680 - (k + 1) * 200 });
      for (let k = 0; k < sideCount && i < n; k++, i++) tables.push({ shape: "rect", capacity, x: 100 + (bottomCount - 1) * 250, y: 680 - (k + 1) * 200 });
      return tables;
    },
  },
  {
    id: "imperial",
    name: "Tavolo imperiale",
    desc: "Un'unica fila lunga di tavoli, come un tavolo imperiale continuo.",
    generate: (n, capacity) => Array.from({ length: n }, (_, i) => ({ shape: "rect", capacity, x: 60 + i * 250, y: 380 })),
  },
  {
    id: "circle",
    name: "Cerchio attorno agli sposi",
    desc: "Tavoli rotondi in cerchio, con un piccolo tavolo per gli sposi al centro.",
    generate: (n, capacity) => {
      const cx = 600, cy = 450;
      // keep adjacent tables at least ~260px apart along the circumference
      const r = Math.max(320, Math.round((n * 260) / (2 * Math.PI)));
      const tables = [{ shape: "round", capacity: 2, x: cx - 59, y: cy - 59 }];
      for (let i = 0; i < n; i++) {
        const angle = (2 * Math.PI * i) / n - Math.PI / 2;
        tables.push({ shape: "round", capacity, x: Math.round(cx + r * Math.cos(angle) - 59), y: Math.round(cy + r * Math.sin(angle) - 59) });
      }
      return tables;
    },
  },
];

function TableToken({ table, selected, onSelect, override }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: table.id });
  const size = tokenSize(table.shape);
  const seats = Array.from({ length: table.capacity }, (_, i) => (table.seats || [])[i] || { guest_id: null, nome: null });
  const occ = seats.filter((s) => s && s.nome).length;
  const positions = seatLabelPositions(size, table.capacity, table.rotation || 0, !!table.seats_ccw);
  // While dragging, snap the visual position to the computed alignment guides
  // instead of following the raw pointer delta (dnd-kit's `transform`).
  const effectiveTransform = override ? { x: override.x - table.x, y: override.y - table.y } : transform;
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onSelect(table.id)}
      role="button"
      className="absolute"
      style={{
        left: table.x,
        top: table.y,
        width: size.w,
        height: size.h,
        transform: effectiveTransform ? `translate(${effectiveTransform.x}px, ${effectiveTransform.y}px) scale(${isDragging ? 1.05 : 1})` : undefined,
        zIndex: isDragging ? 40 : selected ? 25 : 10,
      }}
      data-testid={`table-token-${table.id}`}
    >
      <span
        className="absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white z-10"
        style={{ background: "#1a1a1a" }}
        title="Numero tavolo (ordine di stampa)"
        data-testid={`table-ordine-badge-${table.id}`}
      >
        {table.ordine ?? 0}
      </span>
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center bg-white text-center select-none touch-none cursor-grab active:cursor-grabbing ${table.shape === "round" ? "rounded-full" : "rounded-xl"}`}
        style={{
          borderColor: selected ? "var(--wed-primary)" : "#1a1a1a",
          borderWidth: selected ? 2 : 1,
          borderStyle: "solid",
          boxShadow: isDragging ? "0 12px 30px rgba(0,0,0,0.16)" : "none",
          background: "white",
        }}
      >
        <span className="font-wed text-sm px-2 leading-tight text-[#1a1a1a]">{table.label}</span>
        <span className="text-xs text-neutral-400 mt-0.5" data-testid={`table-occupancy-${table.id}`}>{occ}/{table.capacity}</span>
      </div>
      {positions.map((pos, i) => {
        const seat = seats[i];
        const filled = !!(seat && seat.nome);
        return (
          <span
            key={i}
            className={`absolute pointer-events-none whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded-full border -translate-x-1/2 -translate-y-1/2 ${
              filled ? "bg-white text-neutral-700 font-medium shadow-sm" : "bg-transparent text-neutral-300"
            }`}
            style={{
              left: pos.left,
              top: pos.top,
              borderColor: filled ? "#e3ddd2" : "transparent",
              maxWidth: filled ? 100 : "auto",
              overflow: filled ? "hidden" : "visible",
              textOverflow: "ellipsis",
            }}
            data-testid={`table-seat-label-${table.id}-${i}`}
          >
            {i + 1}{filled ? ` · ${seat.nome}` : ""}
          </span>
        );
      })}
    </div>
  );
}

function RotationDial({ value, onChange, onCommit }) {
  const ref = useRef(null);
  const dragging = useRef(false);

  const angleFromPoint = (clientX, clientY) => {
    const rect = ref.current.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
    if (deg < 0) deg += 360;
    return Math.round(deg);
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    onChange(angleFromPoint(e.clientX, e.clientY));
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    onChange(angleFromPoint(e.clientX, e.clientY));
  };
  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    onCommit();
  };

  const rad = ((value - 90) * Math.PI) / 180;
  const dotX = 26 + 19 * Math.cos(rad);
  const dotY = 26 + 19 * Math.sin(rad);

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className="relative w-[52px] h-[52px] rounded-full border-2 bg-[#f5f3ec] cursor-grab active:cursor-grabbing shrink-0 touch-none"
      style={{ borderColor: "#e3ddd2" }}
      data-testid="table-rotation-dial"
    >
      <RotateCw size={14} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-neutral-300 pointer-events-none" />
      <div
        className="absolute w-2.5 h-2.5 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ left: dotX, top: dotY, background: "var(--wed-primary)" }}
      />
    </div>
  );
}

// Small schematic SVG preview of a template's layout (8 tables as a representative
// sample), so the user can see the shape of the arrangement before applying it.
function TemplatePreview({ template }) {
  const specs = useMemo(() => template.generate(8, 8), [template]);
  const boxes = specs.map((s) => ({ ...s, size: tokenSize(s.shape) }));
  const minX = Math.min(...boxes.map((b) => b.x));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxX = Math.max(...boxes.map((b) => b.x + b.size.w));
  const maxY = Math.max(...boxes.map((b) => b.y + b.size.h));
  const pad = 10;
  const vbW = maxX - minX + pad * 2;
  const vbH = maxY - minY + pad * 2;
  return (
    <svg viewBox={`0 0 ${vbW} ${vbH}`} className="w-16 h-14 shrink-0 rounded-lg border bg-[#faf9f6]" style={{ borderColor: "#e3ddd2" }} preserveAspectRatio="xMidYMid meet">
      {boxes.map((b, i) => {
        const x = b.x - minX + pad;
        const y = b.y - minY + pad;
        return b.shape === "round" ? (
          <circle key={i} cx={x + b.size.w / 2} cy={y + b.size.h / 2} r={b.size.w / 2} fill="white" stroke="var(--wed-primary)" strokeWidth={3} />
        ) : (
          <rect key={i} x={x} y={y} width={b.size.w} height={b.size.h} rx={10} fill="white" stroke="var(--wed-primary)" strokeWidth={3} />
        );
      })}
    </svg>
  );
}

function TemplateModal({ onClose, onApply, defaultCount }) {
  const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
  const [count, setCount] = useState(defaultCount);
  const [capacity, setCapacity] = useState(8);
  const [applying, setApplying] = useState(false);
  const template = TEMPLATES.find((t) => t.id === templateId);

  const apply = async () => {
    setApplying(true);
    await onApply(template, Math.max(1, count), Math.max(1, Math.min(20, capacity)));
    setApplying(false);
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="template-modal">
      <div
        className="w-full max-w-lg bg-[#faf9f6] rounded-3xl p-7 space-y-5 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <div>
            <p className="eyebrow mb-1">Disposizione automatica</p>
            <h3 className="font-wed text-2xl">Usa un template</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="template-modal-close"><X size={20} /></button>
        </div>

        <div className="space-y-2">
          {TEMPLATES.map((t) => (
            <label
              key={t.id}
              className="flex items-start gap-3 border rounded-2xl p-3 cursor-pointer transition-colors duration-150"
              style={{ borderColor: templateId === t.id ? "var(--wed-primary)" : "#e3ddd2", background: templateId === t.id ? "#faf6ee" : "white" }}
              data-testid={`template-option-${t.id}`}
            >
              <input type="radio" name="template" className="mt-1" checked={templateId === t.id} onChange={() => setTemplateId(t.id)} />
              <TemplatePreview template={t} />
              <div>
                <p className="font-medium text-sm">{t.name}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{t.desc}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-wed">Numero tavoli</label>
            <input className="input-wed" type="number" min={1} value={count} onChange={(e) => setCount(Number(e.target.value) || 1)} data-testid="template-count-input" />
          </div>
          <div>
            <label className="label-wed">Posti per tavolo</label>
            <input className="input-wed" type="number" min={1} max={20} value={capacity} onChange={(e) => setCapacity(Number(e.target.value) || 8)} data-testid="template-capacity-input" />
          </div>
        </div>

        <button className="btn-wed w-full" onClick={apply} disabled={applying} data-testid="template-apply-button">
          {applying ? "Applico…" : `Aggiungi ${count} tavoli alla sala attiva`}
        </button>
      </div>
    </div>
  );
}

export default function TablesTab({ wedding }) {
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [tables, setTables] = useState([]);
  const [allGuests, setAllGuests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [printMode, setPrintMode] = useState(null); // 'tableau' | 'layout' | null
  const [printData, setPrintData] = useState(null);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    api.get("/owner/rooms").then((r) => {
      setRooms(r.data);
      setActiveRoomId((prev) => prev || r.data[0]?.id || null);
    }).catch(() => {});
    api.get("/owner/guests").then((r) => setAllGuests(r.data)).catch(() => {});
  }, []);

  // Only guests attending a moment that actually needs a table seat (e.g. Pranzo) show
  // up here -- someone invited only for, say, the evening cake-cutting typically isn't
  // assigned a formal seat in real wedding planning. If no moment is flagged this way,
  // fall back to showing everyone so the designer never ends up empty by surprise.
  const seatRequiredMomentIds = useMemo(() => {
    const moments = wedding?.moments || [];
    return new Set(moments.filter((m) => m.requiresSeat !== false).map((m) => m.id));
  }, [wedding]);

  const guests = useMemo(() => {
    if (seatRequiredMomentIds.size === 0) return allGuests;
    return allGuests.filter((g) => !g.moments?.length || g.moments.some((id) => seatRequiredMomentIds.has(id)));
  }, [allGuests, seatRequiredMomentIds]);

  const openPrint = async (mode) => {
    setShowPrintMenu(false);
    try {
      const roomsWithTables = await Promise.all(
        rooms.map((room) => api.get("/owner/tables", { params: { room_id: room.id } }).then((r) => ({ room, tables: r.data }))),
      );
      setPrintData(roomsWithTables);
      setPrintMode(mode);
    } catch {
      toast.error("Errore nel caricamento dei dati da stampare");
    }
  };

  // Seat labels store a snapshot of the guest's name taken when assigned, so a
  // rename in the guest list doesn't retroactively touch tables already saved
  // before this was kept in sync automatically -- this re-pulls it from the
  // current guest list for every already-assigned seat in the wedding.
  const syncNames = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/owner/tables/sync-names");
      loadTables();
      toast.success(data.updated > 0 ? `${data.updated} posti aggiornati` : "Tutti i nomi erano già aggiornati");
    } catch {
      toast.error("Errore nella sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  const loadTables = useCallback(() => {
    if (!activeRoomId) return;
    api.get("/owner/tables", { params: { room_id: activeRoomId } }).then((r) => setTables(r.data)).catch(() => {});
  }, [activeRoomId]);
  useEffect(() => { loadTables(); setSelectedId(null); }, [loadTables]);

  const addRoom = async () => {
    const nome = window.prompt("Nome della nuova sala (es. Terrazza, Giardino)")?.trim();
    if (!nome) return;
    try {
      const { data } = await api.post("/owner/rooms", { nome });
      setRooms((rs) => [...rs, data]);
      setActiveRoomId(data.id);
    } catch {
      toast.error("Errore nella creazione della sala");
    }
  };

  const renameRoom = async (room) => {
    const nome = window.prompt("Nuovo nome sala", room.nome)?.trim();
    if (!nome || nome === room.nome) return;
    try {
      await api.put(`/owner/rooms/${room.id}`, { nome });
      setRooms((rs) => rs.map((r) => (r.id === room.id ? { ...r, nome } : r)));
    } catch {
      toast.error("Errore nel rinominare la sala");
    }
  };

  const deleteRoom = async (room) => {
    if (rooms.length <= 1) return toast.error("Non puoi eliminare l'unica sala");
    if (!window.confirm(`Eliminare "${room.nome}" e tutti i suoi tavoli?`)) return;
    try {
      await api.delete(`/owner/rooms/${room.id}`);
      const remaining = rooms.filter((r) => r.id !== room.id);
      setRooms(remaining);
      if (activeRoomId === room.id) setActiveRoomId(remaining[0]?.id || null);
      toast.success("Sala eliminata");
    } catch {
      toast.error("Errore nell'eliminazione della sala");
    }
  };

  const applyTemplate = async (template, count, capacity) => {
    const specs = template.generate(count, capacity);
    try {
      const results = await Promise.allSettled(
        specs.map((spec, i) =>
          api.post("/owner/tables", {
            label: `Tavolo ${tables.length + i + 1}`,
            shape: spec.shape,
            capacity: spec.capacity,
            x: spec.x,
            y: spec.y,
            room_id: activeRoomId,
          }),
        ),
      );
      const created = results.filter((r) => r.status === "fulfilled").map((r) => r.value.data);
      const failed = results.length - created.length;
      setTables((ts) => [...ts, ...created]);
      if (created.length) toast.success(`${created.length} tavoli aggiunti`);
      if (failed) toast.error(`${failed} tavoli non creati`);
      setShowTemplates(false);
    } catch {
      toast.error("Errore nell'applicazione del template");
    }
  };

  const selected = tables.find((t) => t.id === selectedId) || null;

  const patchTable = useCallback((id, patch, persist = true) => {
    setTables((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    if (persist) api.put(`/owner/tables/${id}`, patch).catch(() => toast.error("Errore nel salvataggio"));
  }, []);

  // Canva-style alignment: snaps the moving table's edges/centers to whichever
  // edge/center of another table (or the canvas' own center) is within
  // SNAP_THRESHOLD, and reports which guide line(s) matched so they can be drawn.
  const snapToGuides = useCallback((movingShape, rawX, rawY, others) => {
    const size = tokenSize(movingShape);
    const left = rawX, right = rawX + size.w, cx = rawX + size.w / 2;
    const top = rawY, bottom = rawY + size.h, cy = rawY + size.h / 2;

    let bestVDiff = Infinity, bestVGuide = null, bestVOffset = 0;
    let bestHDiff = Infinity, bestHGuide = null, bestHOffset = 0;

    const candidates = [...others.map((o) => ({ ...tokenSize(o.shape), x: o.x, y: o.y })), null];
    candidates.forEach((o) => {
      const vPairs = o
        ? [[left, o.x], [right, o.x + o.w], [cx, o.x + o.w / 2]]
        : [[cx, CANVAS_W / 2]];
      const hPairs = o
        ? [[top, o.y], [bottom, o.y + o.h], [cy, o.y + o.h / 2]]
        : [[cy, CANVAS_H / 2]];
      vPairs.forEach(([val, oval]) => {
        const diff = Math.abs(val - oval);
        if (diff <= SNAP_THRESHOLD && diff < bestVDiff) { bestVDiff = diff; bestVGuide = oval; bestVOffset = val - oval; }
      });
      hPairs.forEach(([val, oval]) => {
        const diff = Math.abs(val - oval);
        if (diff <= SNAP_THRESHOLD && diff < bestHDiff) { bestHDiff = diff; bestHGuide = oval; bestHOffset = val - oval; }
      });
    });

    const x = bestVGuide !== null ? rawX - bestVOffset : rawX;
    const y = bestHGuide !== null ? rawY - bestHOffset : rawY;
    return { x, y, vGuide: bestVGuide, hGuide: bestHGuide };
  }, []);

  // Light separation only: nudges the table out of an actual overlap with a
  // neighbour, using a small margin rather than the full label radius, so tables
  // can still sit close together without being shoved far apart.
  const separateFromOverlap = (movingShape, x, y, others) => {
    const size = tokenSize(movingShape);
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const o of others) {
        const oSize = tokenSize(o.shape);
        const ax1 = x - OVERLAP_PAD, ay1 = y - OVERLAP_PAD, ax2 = x + size.w + OVERLAP_PAD, ay2 = y + size.h + OVERLAP_PAD;
        const bx1 = o.x - OVERLAP_PAD, by1 = o.y - OVERLAP_PAD, bx2 = o.x + oSize.w + OVERLAP_PAD, by2 = o.y + oSize.h + OVERLAP_PAD;
        const overlapX = Math.min(ax2, bx2) - Math.max(ax1, bx1);
        const overlapY = Math.min(ay2, by2) - Math.max(ay1, by1);
        if (overlapX > 0 && overlapY > 0) {
          moved = true;
          const cx = x + size.w / 2, cy = y + size.h / 2;
          const ocx = o.x + oSize.w / 2, ocy = o.y + oSize.h / 2;
          if (overlapX < overlapY) x += (cx >= ocx ? 1 : -1) * (overlapX + 1);
          else y += (cy >= ocy ? 1 : -1) * (overlapY + 1);
        }
      }
      if (!moved) break;
    }
    return { x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) };
  };

  const [dragPreview, setDragPreview] = useState(null); // { id, x, y }
  const [guides, setGuides] = useState({ v: null, h: null });

  const onDragMove = ({ active, delta }) => {
    const t = tables.find((x) => x.id === active.id);
    if (!t) return;
    const others = tables.filter((o) => o.id !== t.id);
    const { x, y, vGuide, hGuide } = snapToGuides(t.shape, t.x + delta.x, t.y + delta.y, others);
    setDragPreview({ id: t.id, x, y });
    setGuides({ v: vGuide, h: hGuide });
  };

  const clearDragGuides = () => { setDragPreview(null); setGuides({ v: null, h: null }); };

  const onDragEnd = ({ active, delta }) => {
    const t = tables.find((x) => x.id === active.id);
    clearDragGuides();
    if (!t || (delta.x === 0 && delta.y === 0)) return;
    const others = tables.filter((o) => o.id !== t.id);
    const snapped = snapToGuides(t.shape, t.x + delta.x, t.y + delta.y, others);
    const { x: nx, y: ny } = separateFromOverlap(t.shape, snapped.x, snapped.y, others);
    patchTable(active.id, { x: nx, y: ny });
  };

  const addTable = async (shape) => {
    try {
      const n = tables.length;
      const { data } = await api.post("/owner/tables", {
        label: `Tavolo ${n + 1}`,
        shape,
        capacity: 8,
        x: 40 + (n % 5) * 210,
        y: 40 + Math.floor(n / 5) * 200,
        room_id: activeRoomId,
      });
      setTables((ts) => [...ts, data]);
      setSelectedId(data.id);
    } catch {
      toast.error("Errore nella creazione del tavolo");
    }
  };

  const deleteTable = async (id) => {
    try {
      await api.delete(`/owner/tables/${id}`);
      setTables((ts) => ts.filter((t) => t.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Tavolo eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  // guest ids seated in the ACTIVE room's tables (exact table id, used to clear the old seat)
  const seatedElsewhere = useMemo(() => {
    const map = {};
    tables.forEach((t) => (t.seats || []).forEach((s) => { if (s && s.guest_id) map[s.guest_id] = t.id; }));
    return map;
  }, [tables]);

  // guests seated ANYWHERE in the wedding (any room) -- from /owner/guests, which is
  // wedding-wide. Catches cross-room duplicates the active room's own tables can't see;
  // the backend enforces the one-seat-per-wedding rule regardless, this only drives the
  // "already assigned" hint in the dropdown.
  const seatedAnywhere = useMemo(() => new Set(guests.filter((g) => g.numero_tavolo).map((g) => g.id)), [guests]);

  // one pseudo-option per "ospite extra" (+1, +2, ...) of each guest, so their
  // companions can also get their own seat even though they have no guest record
  const extraSlots = useMemo(() => {
    const out = [];
    guests.forEach((g) => {
      for (let n = 1; n <= (g.num_ospiti_extra || 0); n++) {
        out.push({ value: `extra:${g.id}:${n}`, nome: `${g.nome} (+${n})` });
      }
    });
    return out;
  }, [guests]);

  // free-text seat names (no guest_id) already placed elsewhere -- covers the
  // "ospite extra" pseudo-options above, which have no guest_id to dedupe by
  const freeNameElsewhere = useMemo(() => {
    const map = {};
    tables.forEach((t) =>
      (t.seats || []).forEach((s) => { if (s && s.nome && !s.guest_id) map[s.nome] = t.id; }),
    );
    return map;
  }, [tables]);

  const saveSeats = (tableId, seats) => {
    const clean = seats.map((s) => (s ? { guest_id: s.guest_id || null, nome: s.nome || null } : { guest_id: null, nome: null }));
    patchTable(tableId, { seats: clean });
  };

  const setSeat = (seatIndex, value) => {
    if (!selected) return;
    const seats = Array.from({ length: selected.capacity }, (_, i) => selected.seats[i] || { guest_id: null, nome: null });
    seats[seatIndex] = value;
    saveSeats(selected.id, seats);
  };

  // frees `matcher(seat)` from whichever OTHER table currently holds it
  const freeFromOtherTable = (otherTableId, matcher) => {
    if (!otherTableId || otherTableId === selected.id) return;
    const other = tables.find((t) => t.id === otherTableId);
    if (!other) return;
    const cleaned = other.seats.map((s) => (s && matcher(s) ? { guest_id: null, nome: null } : s));
    patchTable(other.id, { seats: cleaned.map((s) => (s ? { guest_id: s.guest_id || null, nome: s.nome || null } : { guest_id: null, nome: null })) });
  };

  const assignGuestToSeat = async (seatIndex, value) => {
    if (!selected) return;
    if (!value) { setSeat(seatIndex, { guest_id: null, nome: null }); return; }
    if (value.startsWith("extra:")) {
      const slot = extraSlots.find((e) => e.value === value);
      if (!slot) return;
      freeFromOtherTable(freeNameElsewhere[slot.nome], (s) => !s.guest_id && s.nome === slot.nome);
      setSeat(seatIndex, { guest_id: null, nome: slot.nome });
      return;
    }
    const guest = guests.find((g) => g.id === value);
    if (!guest) return;
    freeFromOtherTable(seatedElsewhere[value], (s) => s.guest_id === value);
    setSeat(seatIndex, { guest_id: value, nome: guest.nome });
  };

  const seatRows = selected ? Array.from({ length: selected.capacity }, (_, i) => selected.seats[i] || { guest_id: null, nome: null }) : [];
  const occupied = selected ? selected.seats.filter((s) => s && s.nome).length : 0;

  const unassignedGuests = useMemo(() => guests.filter((g) => !g.numero_tavolo), [guests]);

  const confirmedHeadcount = guests
    .filter((g) => g.stato_rsvp === "confermato")
    .reduce((s, g) => s + 1 + (g.num_ospiti_extra || 0), 0);
  const defaultTemplateCount = Math.max(1, Math.ceil((confirmedHeadcount || 1) / 8));

  return (
    <div data-testid="tables-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-wed text-2xl">Designer tavoli</h2>
          <p className="text-sm text-neutral-500 mt-1">Aggiungi i tavoli, trascinali per disporre la sala e assegna gli invitati ai posti.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2"
            onClick={syncNames}
            disabled={syncing}
            title="Aggiorna i nomi ai tavoli se hai rinominato un invitato"
            data-testid="sync-seat-names-button"
          >
            <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> Sincronizza nomi
          </button>
          <button
            className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2"
            onClick={() => setShowTemplates(true)}
            data-testid="open-template-modal-button"
          >
            <LayoutGrid size={15} /> Usa un template
          </button>
          <div className="relative">
            <button
              className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2"
              onClick={() => setShowPrintMenu((s) => !s)}
              data-testid="open-print-menu-button"
            >
              <Printer size={15} /> Stampa <ChevronDown size={14} />
            </button>
            {showPrintMenu && (
              <div className="absolute right-0 mt-1.5 w-64 bg-white border rounded-xl shadow-lg overflow-hidden z-30" style={{ borderColor: "#e3ddd2" }} data-testid="print-menu">
                <button onClick={() => openPrint("tableau")} className="w-full text-left px-4 py-3 text-sm hover:bg-neutral-50 transition-colors duration-150" data-testid="print-tableau-button">
                  Tableau de mariage (lista per tavolo)
                </button>
                <button onClick={() => openPrint("layout")} className="w-full text-left px-4 py-3 text-sm hover:bg-neutral-50 border-t transition-colors duration-150" style={{ borderColor: "#eee8dd" }} data-testid="print-layout-button">
                  Disposizione tavoli (piantina)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {printMode === "tableau" && printData && (
        <TableauPrint wedding={wedding} roomsWithTables={printData} unassignedGuests={unassignedGuests} onClose={() => setPrintMode(null)} />
      )}
      {printMode === "layout" && printData && (
        <FloorPlanPrint wedding={wedding} roomsWithTables={printData} onClose={() => setPrintMode(null)} />
      )}

      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="room-switcher">
        {rooms.map((r) => (
          <div
            key={r.id}
            className="group flex items-center gap-1.5 rounded-full border pl-4 pr-2 py-1.5 text-sm transition-colors duration-150"
            style={activeRoomId === r.id ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)", color: "white" } : { borderColor: "#e3ddd2", background: "white", color: "#525252" }}
          >
            <button onClick={() => setActiveRoomId(r.id)} data-testid={`room-tab-${r.id}`}>{r.nome}</button>
            {activeRoomId === r.id && (
              <span className="flex items-center gap-0.5">
                <button onClick={() => renameRoom(r)} className="p-0.5 opacity-70 hover:opacity-100" data-testid={`room-rename-${r.id}`}><Pencil size={12} /></button>
                <button onClick={() => deleteRoom(r)} className="p-0.5 opacity-70 hover:opacity-100" data-testid={`room-delete-${r.id}`}><Trash2 size={12} /></button>
              </span>
            )}
          </div>
        ))}
        <button
          onClick={addRoom}
          className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border border-dashed text-neutral-400 hover:text-neutral-700 transition-colors duration-150"
          style={{ borderColor: "#e3ddd2" }}
          data-testid="room-add-button"
        >
          <Plus size={14} /> Sala
        </button>
      </div>

      {showTemplates && (
        <TemplateModal onClose={() => setShowTemplates(false)} onApply={applyTemplate} defaultCount={defaultTemplateCount} />
      )}

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        {/* Canvas */}
        <div className="relative min-w-0">
          <DndContext sensors={sensors} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={clearDragGuides}>
            <div
              className="relative rounded-3xl border overflow-auto"
              style={{
                borderColor: "#e3ddd2",
                background: "#f5f3ec",
                backgroundImage: "radial-gradient(#e3ddd2 1px, transparent 1px)",
                backgroundSize: "24px 24px",
                height: 600,
              }}
              onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
              data-testid="table-designer-canvas"
            >
              <div className="relative" style={{ width: CANVAS_W, height: CANVAS_H }}>
                {tables.map((t) => (
                  <TableToken
                    key={t.id}
                    table={t}
                    selected={t.id === selectedId}
                    onSelect={setSelectedId}
                    override={dragPreview && dragPreview.id === t.id ? dragPreview : null}
                  />
                ))}
                {guides.v !== null && (
                  <div
                    className="absolute top-0 pointer-events-none"
                    style={{ left: guides.v, width: 0, height: CANVAS_H, borderLeft: "1.5px dashed #ff4d6d", zIndex: 50 }}
                    data-testid="align-guide-v"
                  />
                )}
                {guides.h !== null && (
                  <div
                    className="absolute left-0 pointer-events-none"
                    style={{ top: guides.h, height: 0, width: CANVAS_W, borderTop: "1.5px dashed #ff4d6d", zIndex: 50 }}
                    data-testid="align-guide-h"
                  />
                )}
                {tables.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="tables-empty">
                    <p className="text-neutral-400 text-sm text-center max-w-xs">La sala è vuota.<br />Aggiungi il primo tavolo dalla barra qui sotto.</p>
                  </div>
                )}
              </div>
            </div>
          </DndContext>

          {/* Floating toolbar */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-4 flex items-center gap-1 bg-white/90 backdrop-blur-xl border rounded-full px-2 py-2 shadow-md" style={{ borderColor: "#e3ddd2" }} data-testid="table-toolbar">
            <button onClick={() => addTable("round")} className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full hover:bg-[#f5f3ec] transition-colors duration-150" data-testid="add-round-table-btn">
              <Circle size={16} strokeWidth={1.5} /> Tondo
            </button>
            <button onClick={() => addTable("square")} className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full hover:bg-[#f5f3ec] transition-colors duration-150" data-testid="add-square-table-btn">
              <Square size={16} strokeWidth={1.5} /> Quadrato
            </button>
            <button onClick={() => addTable("rect")} className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full hover:bg-[#f5f3ec] transition-colors duration-150" data-testid="add-rect-table-btn">
              <RectangleHorizontal size={16} strokeWidth={1.5} /> Rettangolare
            </button>
          </div>
        </div>

        {/* Properties panel */}
        <aside className="bg-white border rounded-3xl p-5 h-fit lg:sticky lg:top-28" style={{ borderColor: "#e3ddd2" }} data-testid="table-properties-panel">
          {!selected ? (
            <p className="text-sm text-neutral-400 py-10 text-center">Seleziona un tavolo per modificarne i dettagli e assegnare gli invitati.</p>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-wed text-lg">Dettagli tavolo</h3>
                <button onClick={() => deleteTable(selected.id)} className="text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid="delete-table-btn"><Trash2 size={17} /></button>
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-3 mb-4">
                <div>
                  <label className="label-wed">Nome</label>
                  <input
                    className="input-wed"
                    value={selected.label}
                    onChange={(e) => patchTable(selected.id, { label: e.target.value }, false)}
                    onBlur={(e) => patchTable(selected.id, { label: e.target.value.trim() || "Tavolo" })}
                    data-testid="table-label-input"
                  />
                </div>
                <div className="w-20">
                  <label className="label-wed">Numero</label>
                  <input
                    className="input-wed"
                    type="number"
                    value={selected.ordine ?? 0}
                    onChange={(e) => patchTable(selected.id, { ordine: Number(e.target.value) || 0 }, false)}
                    onBlur={(e) => patchTable(selected.id, { ordine: Number(e.target.value) || 0 })}
                    title="Determina l'ordine di stampa del tableau de mariage"
                    data-testid="table-ordine-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="label-wed">Forma</label>
                  <select className="input-wed" value={selected.shape} onChange={(e) => patchTable(selected.id, { shape: e.target.value })} data-testid="table-shape-select">
                    {Object.entries(SHAPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-wed">Posti</label>
                  <input
                    className="input-wed"
                    type="number"
                    min={1}
                    max={20}
                    value={selected.capacity}
                    onChange={(e) => {
                      const cap = Math.max(1, Math.min(20, Number(e.target.value) || 1));
                      const removed = (selected.seats || []).slice(cap).filter((s) => s && s.nome);
                      const seats = (selected.seats || []).slice(0, cap);
                      if (removed.length) toast.warning(`${removed.length} invitato/i rimosso/i dai posti: ${removed.map((s) => s.nome).join(", ")}`);
                      patchTable(selected.id, { capacity: cap, seats });
                    }}
                    data-testid="table-capacity-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <RotationDial
                  value={selected.rotation || 0}
                  onChange={(deg) => patchTable(selected.id, { rotation: deg }, false)}
                  onCommit={() => patchTable(selected.id, { rotation: Math.round(selected.rotation || 0) })}
                />
                <div>
                  <label className="label-wed !mb-0.5">Rotazione numerazione</label>
                  <p className="text-xs text-neutral-400">Trascina la rotellina per far partire il posto 1 dal punto giusto ({Math.round(selected.rotation || 0)}°).</p>
                  <label className="flex items-center gap-2 mt-2 text-sm text-neutral-600 cursor-pointer" data-testid="table-ccw-toggle">
                    <input
                      type="checkbox"
                      checked={!!selected.seats_ccw}
                      onChange={(e) => patchTable(selected.id, { seats_ccw: e.target.checked })}
                    />
                    Numera in senso antiorario
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between mb-2">
                <label className="label-wed !mb-0">Posti a sedere</label>
                <span className="text-xs text-neutral-400">{occupied}/{selected.capacity}</span>
              </div>
              <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1" data-testid="seat-list">
                {seatRows.map((seat, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`seat-row-${i}`}>
                    <span className="w-5 text-xs text-neutral-400 shrink-0">{i + 1}</span>
                    <select
                      className="input-wed !py-2 text-sm flex-1"
                      value={
                        seat.guest_id ||
                        (seat.nome ? extraSlots.find((e) => e.nome === seat.nome)?.value || "__free__" : "")
                      }
                      onChange={(e) => {
                        if (e.target.value === "__free__") return;
                        assignGuestToSeat(i, e.target.value || null);
                      }}
                      data-testid={`seat-guest-select-${i}`}
                    >
                      <option value="">— vuoto —</option>
                      {seat.nome && !seat.guest_id && !extraSlots.some((e) => e.nome === seat.nome) && (
                        <option value="__free__">{seat.nome} (manuale)</option>
                      )}
                      {guests.map((g) => {
                        const inThisTable = seatedElsewhere[g.id] === selected.id;
                        const elsewhere = !inThisTable && (seatedElsewhere[g.id] || seatedAnywhere.has(g.id));
                        return <option key={g.id} value={g.id} disabled={elsewhere}>{g.nome}{elsewhere ? " • assegnato" : ""}</option>;
                      })}
                      {extraSlots.length > 0 && (
                        <optgroup label="Accompagnatori">
                          {extraSlots.map((ex) => {
                            const elsewhere = freeNameElsewhere[ex.nome] && freeNameElsewhere[ex.nome] !== selected.id;
                            return (
                              <option key={ex.value} value={ex.value} disabled={elsewhere}>
                                {ex.nome}{elsewhere ? " • assegnato" : ""}
                              </option>
                            );
                          })}
                        </optgroup>
                      )}
                    </select>
                  </div>
                ))}
              </div>

              <p className="text-xs text-neutral-400 mt-3 flex items-center gap-1"><UserRound size={12} /> Assegna un invitato dalla lista a ogni posto — gli accompagnatori (+1, +2…) sono sotto "Accompagnatori".</p>
              {allGuests.length > guests.length && (
                <p className="text-xs text-neutral-400 mt-1">{allGuests.length - guests.length} invitati non in lista perché non partecipano a un momento che richiede posto a tavola (vedi Pagina & Tema → Momenti dell'evento).</p>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
